import { z } from "zod";

// ── Pipelines & owners ───────────────────────────────────────────────

/** The id of the virtual pipeline an environment without an opportunity BPF gets. */
export const MSDYNAMICS_FALLBACK_PIPELINE_ID = "salesstagecode";

/** Stage ids of that virtual pipeline: `salesstagecode:<optionValue>`. */
export const MSDYNAMICS_FALLBACK_STAGE_PREFIX = `${MSDYNAMICS_FALLBACK_PIPELINE_ID}:`;

/**
 * How `defaultPipelineId` / `defaultStageId` must be read. The API echoes it on
 * `deal-config`, so the picker never re-derives it from the id shape.
 */
export enum MsdynamicsPipelineKind {
	BUSINESS_PROCESS_FLOW = "BUSINESS_PROCESS_FLOW",
	SALES_STAGE = "SALES_STAGE",
}

export const MsdynamicsPipelineStageSchema = z.object({
	id: z.string(),
	label: z.string(),
	displayOrder: z.coerce.number().optional(),
});

export type MsdynamicsPipelineStage = z.infer<typeof MsdynamicsPipelineStageSchema>;

export const MsdynamicsPipelineSchema = z.object({
	id: z.string(),
	label: z.string(),
	stages: z.array(MsdynamicsPipelineStageSchema),
});

export type MsdynamicsPipeline = z.infer<typeof MsdynamicsPipelineSchema>;

export const MsdynamicsPipelinesResponseSchema = z.object({
	pipelines: z.array(MsdynamicsPipelineSchema),
});

export type MsdynamicsPipelinesResponse = z.infer<typeof MsdynamicsPipelinesResponseSchema>;

export const MsdynamicsOwnerSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
});

export type MsdynamicsOwner = z.infer<typeof MsdynamicsOwnerSchema>;

export const MsdynamicsOwnersResponseSchema = z.object({
	owners: z.array(MsdynamicsOwnerSchema),
});

export type MsdynamicsOwnersResponse = z.infer<typeof MsdynamicsOwnersResponseSchema>;

// ── Opportunity / lead configuration ─────────────────────────────────

/**
 * There is deliberately **no `defaultCurrency`**: Dataverse takes an opportunity's
 * currency from `transactioncurrencyid`, a lookup, which cannot be a configuration string.
 */
export const MsdynamicsDealConfigResponseSchema = z.object({
	defaultPipelineId: z.string().nullish(),
	pipelineKind: z.nativeEnum(MsdynamicsPipelineKind).nullish(),
	defaultStageId: z.string().nullish(),
	dealNameTemplate: z.string(),
	expectedCloseDateOffset: z.coerce.number(),
	defaultOwnerId: z.string().nullish(),
});

export type MsdynamicsDealConfigResponse = z.infer<typeof MsdynamicsDealConfigResponseSchema>;

/**
 * `PUT deal-config` patches: every field is `@IsOptional() @IsString()`, so an unset value
 * must be **omitted**, never sent as `null` (unlike the Pipedrive endpoint).
 */
export const UpdateMsdynamicsDealConfigSchema = z.object({
	defaultPipelineId: z.string().optional(),
	defaultStageId: z.string().optional(),
	dealNameTemplate: z.string().optional(),
	expectedCloseDateOffset: z.number().optional(),
	defaultOwnerId: z.string().optional(),
});

export type UpdateMsdynamicsDealConfig = z.infer<typeof UpdateMsdynamicsDealConfigSchema>;

/**
 * `estimatedValue` is a `DECIMAL`, which the MySQL driver hands back as a **string** —
 * coerced here so consumers can do arithmetic on it (web §6).
 *
 * Two Pipedrive fields are absent by design: `defaultCurrency` (see above) and
 * `defaultLabelIds` (Dataverse has no lead labels — `leadqualitycode` / `leadsourcecode`
 * are ordinary field-mapping targets). The lead's title is Dataverse's `subject`, hence
 * `leadTopicTemplate`; the sync falls back to `dealNameTemplate` when it is unset.
 */
export const MsdynamicsLeadConfigResponseSchema = z.object({
	leadTopicTemplate: z.string().nullish(),
	defaultOwnerId: z.string().nullish(),
	estimatedValue: z.coerce.number().nullable().optional(),
	expectedCloseDateOffset: z.coerce.number().nullable().optional(),
});

export type MsdynamicsLeadConfigResponse = z.infer<typeof MsdynamicsLeadConfigResponseSchema>;

export const UpdateMsdynamicsLeadConfigSchema = z.object({
	leadTopicTemplate: z.string().optional(),
	defaultOwnerId: z.string().optional(),
	estimatedValue: z.number().optional(),
	expectedCloseDateOffset: z.number().optional(),
});

export type UpdateMsdynamicsLeadConfig = z.infer<typeof UpdateMsdynamicsLeadConfigSchema>;
